import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Form, Field } from "react-final-form";
import axios from "axios";
import "./ProfileDetailed.css";
import "./Profile.css";
import { FaUser, FaEnvelope, FaLock, FaPlus } from "react-icons/fa";
import { updateUser } from "../../store/slices/authSlice";
import UserAvatar from "../../components/UserAvatar";
import { useTranslation } from 'react-i18next'; // added for i18n

// Gender mapping: 1-MALE, 2-FEMALE, 3-OTHER
const genderMap = {
  1: "Male",
  2: "Female",
  3: "Other",
};

// Reverse mapping: string to integer
const genderStringToInt = {
  "Male": 1,
  "Female": 2,
  "Other": 3,
};

// Employment type mapping: 1-FULL_TIME, 2-PART_TIME, 3-CONTRACTOR, 4-TEMPORARY, 5-INTERNSHIP, 6-OTHER
const empTypeMap = {
  1: "Full Time",
  2: "Part Time",
  3: "Contractor",
  4: "Temporary",
  5: "Internship",
  6: "Other",
};


const Profile = () => {
  const dispatch = useDispatch();
  const { user, token } = useSelector((state) => state.auth);
  const { t } = useTranslation();
  const API_BASE_URL = process.env.REACT_APP_EZTS_API_BASE_URL;
  const ADMIN_BASE_URL = process.env.REACT_APP_EZTS_ADMIN_BASE_URL || 'https://timesheet-admin.oseelight.com';

  // Map Redux user into the profile shape used by this component
  const profile = user
    ? {
        username: user.userName, // from API
        title: user.title?.titleName,
        department: user.department?.deptName,
        employmentType: empTypeMap[user.empTypeId] || "N/A",
        contractStartDate: user.contractStartDate || "N/A",
        contractEndDate: user.contractEndDate || "N/A",
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        gender: user.gender && genderMap[user.gender] ? genderMap[user.gender] : "",
      }
    : null;

  const [personalMessage, setPersonalMessage] = useState("");
  const [personalMessageError, setPersonalMessageError] = useState(false);
  const [emailMessage, setEmailMessage] = useState("");
  const [emailMessageError, setEmailMessageError] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState("");
  const [passwordMessageError, setPasswordMessageError] = useState(false);
  const [avatarMessage, setAvatarMessage] = useState("");
  const avatarInputRef = React.useRef(null);

  const validate = (values) => {
    const errors = {};
    if (!values.firstName) errors.firstName = t('profile.firstNameRequired');
    if (!values.lastName) errors.lastName = t('profile.lastNameRequired');
    if (!values.username) errors.username = t('profile.usernameRequired');
    if (
      values.email &&
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)
    ) {
      errors.email = t('profile.invalidEmail');
    }
    return errors;
  };

  const validateEmailOnly = (values) => {
    const errors = {};
    if (!values.email) errors.email = t('profile.emailRequired');
    if (
      values.email &&
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)
    ) {
      errors.email = t('profile.invalidEmail');
    }
    return errors;
  };

  const validatePasswordOnly = (values) => {
    const errors = {};
    if (!values.currentPassword) errors.currentPassword = t('profile.currentPasswordRequired');
    if (!values.newPassword) errors.newPassword = t('profile.newPasswordRequired');
    if (!values.confirmPassword) errors.confirmPassword = t('profile.confirmPasswordRequired');
    if (values.newPassword && values.confirmPassword && values.newPassword !== values.confirmPassword) {
      errors.confirmPassword = t('profile.passwordsNotMatch');
    }
    return errors;
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setAvatarMessage(t('profile.selectImageError'));
      setTimeout(() => setAvatarMessage(""), 3000);
      return;
    }

    if (!user?.userId) {
      setAvatarMessage(t('profile.missingUserId'));
      setTimeout(() => setAvatarMessage(""), 3000);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.put(
        `${API_BASE_URL}user/update-avatar?userId=${user.userId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const orgNum = user.department.costCenter.organization.orgNum;
      const fullAvatarUrl = `${ADMIN_BASE_URL}/upload/${orgNum}/avatar/${response.data.avatar}`;

      dispatch(updateUser({ avatar: fullAvatarUrl }));
      setAvatarMessage(t('profile.profilePhotoUpdated'));
      setTimeout(() => setAvatarMessage(""), 3000);
    } catch (error) {
      const res = error.response;
      console.error("Avatar upload failed:", {
        status: res?.status,
        statusText: res?.statusText,
        data: res?.data,
        message: error.message,
      });

      let errorMessage = "Failed to update profile photo";
      if (res?.data) {
        if (typeof res.data === "string") errorMessage = res.data;
        else if (res.data.message) errorMessage = res.data.message;
        else if (res.data.error) errorMessage = res.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      if (res?.status) {
        errorMessage = `[${res.status}] ${errorMessage}`;
      }

      setAvatarMessage(`Error: ${errorMessage}`);
      setTimeout(() => setAvatarMessage(""), 5000);
    }

    e.target.value = "";
  };
  /*  //avatar remover commented out for now
  // const handleRemoveAvatar = () => {
  //   // dispatch(updateUser({ avatar: null }));
  //   // setAvatarMessage("Profile photo removed");
  //   // setTimeout(() => setAvatarMessage(""), 3000);
  // };

*/
  if (!profile) {
    return <div>{t('profile.noData')}</div>;
  }

  return (
    <div className="profile-detailed-layout">
      {/* Left: Summary */}
      <div className="profile-summary">
        <div>
          <h1>{t('profile.userProfile')}</h1>
          <div className="profile-avatar-wrapper">
            <div className="profile-avatar-ring">
              <UserAvatar avatar={user?.avatar} className="profile-summary-avatar" />
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/*"
                className="profile-avatar-input"
                onChange={handleAvatarChange}
                aria-label={t('profile.uploadPhoto')}
              />
              <button
                type="button"
                className="profile-avatar-add-btn"
                onClick={() => avatarInputRef.current?.click()}
                aria-label={t('profile.changePhoto')}
              >
                <FaPlus />
              </button>
            </div>
           
            {/* {user?.avatar && (
              <button
                type="button"
                className="profile-avatar-remove-link"
                onClick={handleRemoveAvatar}
              >
                Remove photo
              </button>
            )} */}
            
            {avatarMessage && (
              <span className="form-status profile-avatar-status">{avatarMessage}</span>
            )}
          </div>
          <h2>{profile.username}</h2>
          <p>{profile.title}</p>
          <p>{profile.department}</p>
        </div>
        <p>{profile.employmentType}</p>
        <p className="contract">
          {t('profile.contractLength')}
          <br />
          {profile.contractStartDate} - {profile.contractEndDate}
        </p>
      </div>

      {/* Center: Editable Sections */}
      <div className="profile-center">
        {/* Personal Details */}
        <Form
          initialValues={profile}
          validate={validate}
          onSubmit={async (values) => {
            try {
              // Map gender string to integer (only if gender is selected)
              const genderInt = values.gender && genderStringToInt[values.gender] 
                ? genderStringToInt[values.gender] 
                : null;
              
              // Build query parameters
              const params = new URLSearchParams({
                firstName: values.firstName,
                lastName: values.lastName,
                userName: values.username,
              });
              
              // Only add gender if it's selected
              if (genderInt !== null) {
                params.append('gender', genderInt.toString());
              }

              // Make PUT request
              await axios.put(
                `${API_BASE_URL}user/update-details?${params.toString()}`,
                {},
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                }
              );

              // Update Redux state with new values
              const updateData = {
                firstName: values.firstName,
                lastName: values.lastName,
                userName: values.username,
              };
              
              // Only update gender if it was selected
              if (genderInt !== null) {
                updateData.gender = genderInt;
              }
              
              dispatch(updateUser(updateData));

              setPersonalMessage(t('profile.personalDetailsUpdated'));
              setPersonalMessageError(false);
              setTimeout(() => setPersonalMessage(""), 3000);
            } catch (error) {
              console.error("Error updating personal details:", error);
              const errorMessage = error.response?.data?.message || error.message || "Failed to update personal details";
              setPersonalMessage(`Error: ${errorMessage}`);
              setPersonalMessageError(true);
              setTimeout(() => {
                setPersonalMessage("");
                setPersonalMessageError(false);
              }, 5000);
            }
          }}
          render={({ handleSubmit }) => (
            <form onSubmit={handleSubmit} className="profile-section">
              <h2 className="profile-detailed-header">
                <FaUser />
                {t('profile.personalDetails')}
              </h2>
              <div className="input-pair">
                <div className="form-field">
                  <label>{t('profile.firstName')}</label>
                  <Field name="firstName">
                    {({ input, meta }) => (
                      <>
                        <input {...input} type="text" />
                        {meta.touched && meta.error && (
                          <div className="error">{meta.error}</div>
                        )}
                      </>
                    )}
                  </Field>
                </div>
                <div className="form-field">
                  <label>{t('profile.lastName')}</label>
                  <Field name="lastName">
                    {({ input, meta }) => (
                      <>
                        <input {...input} type="text" />
                        {meta.touched && meta.error && (
                          <div className="error">{meta.error}</div>
                        )}
                      </>
                    )}
                  </Field>
                </div>
              </div>

              <div className="input-pair">
                <div className="form-field">
                  <label>{t('profile.username')}</label>
                  <Field name="username">
                    {({ input, meta }) => (
                      <>
                        <input {...input} type="text" />
                        {meta.touched && meta.error && (
                          <div className="error">{meta.error}</div>
                        )}
                      </>
                    )}
                  </Field>
                </div>
                <div className="form-field">
                  <label>{t('profile.gender')}</label>
                  <Field name="gender" component="select">
                    <option value="">{t('profile.selectGender')}</option>
                    <option value="Male">{t('profile.male')}</option>
                    <option value="Female">{t('profile.female')}</option>
                    <option value="Other">{t('profile.other')}</option>
                  </Field>
                </div>
              </div>
              <div className="form-actions">
                <button className="btn primary update" type="submit">
                  {t('profile.update')}
                </button>
                {personalMessage && (
                  <span className={`form-status ${personalMessageError ? 'error' : ''}`} aria-live="polite">
                    {personalMessage}
                  </span>
                )}
              </div>
            </form>
          )}
        />

        {/* Side-by-side Email + Password */}
        <div className="side-by-side-boxes">
          {/* Email Settings */}
          <Form
            initialValues={profile}
            validate={validateEmailOnly}
            onSubmit={async (values) => {
              try {
                const params = new URLSearchParams({ email: values.email });
                await axios.put(
                  `${API_BASE_URL}user/update-email?${params.toString()}`,
                  {},
                  {
                    headers: {
                      Authorization: `Bearer ${token}`,
                    },
                  }
                );

                dispatch(updateUser({ email: values.email }));
                setEmailMessage(t('profile.emailUpdated'));
                setEmailMessageError(false);
                setTimeout(() => setEmailMessage(""), 3000);
              } catch (error) {
                // eslint-disable-next-line no-console
                console.error("Error updating email:", error);
                const errorMessage =
                  error.response?.data?.message ||
                  error.message ||
                  "Failed to update email";
                setEmailMessage(`Error: ${errorMessage}`);
                setEmailMessageError(true);
                setTimeout(() => {
                  setEmailMessage("");
                  setEmailMessageError(false);
                }, 5000);
              }
            }}
            render={({ handleSubmit }) => (
              <form onSubmit={handleSubmit} className="profile-section">
                <h2 className="profile-detailed-header">
                  <FaEnvelope />
                  {t('profile.emailSettings')}
                </h2>
                <div className="form-field">
                  <label>{t('profile.emailAddress')}</label>
                  <Field name="email">
                    {({ input, meta }) => (
                      <>
                        <input {...input} type="email" />
                        {meta.touched && meta.error && (
                          <div className="error">{meta.error}</div>
                        )}
                      </>
                    )}
                  </Field>
                </div>
                <div className="form-actions">
                  <button className="btn primary update" type="submit">
                    {t('profile.update')}
                  </button>
                  {emailMessage && (
                    <span className={`form-status ${emailMessageError ? 'error' : ''}`} aria-live="polite">
                      {emailMessage}
                    </span>
                  )}
                </div>
              </form>
            )}
          />

          {/* Change Password */}
          <Form
            validate={validatePasswordOnly}
            onSubmit={async (values, form) => {
              try {
                const params = new URLSearchParams({
                  oldPassword: values.currentPassword,
                  newPassword: values.newPassword,
                });
                await axios.put(
                  `${API_BASE_URL}user/change-password?${params.toString()}`,
                  {},
                  {
                    headers: {
                      Authorization: `Bearer ${token}`,
                    },
                  }
                );

                setPasswordMessage(t('profile.passwordUpdated'));
                setPasswordMessageError(false);
                setTimeout(() => setPasswordMessage(""), 3000);

                // Clear password fields on success
                form.reset();
              } catch (error) {
                // eslint-disable-next-line no-console
                console.error("Error changing password:", error);
                const errorMessage =
                  error.response?.data?.message ||
                  error.message ||
                  "Failed to change password";
                setPasswordMessage(`Error: ${errorMessage}`);
                setPasswordMessageError(true);
                setTimeout(() => {
                  setPasswordMessage("");
                  setPasswordMessageError(false);
                }, 5000);
              }
            }}
            render={({ handleSubmit }) => (
              <form onSubmit={handleSubmit} className="profile-section">
                <h2 className="profile-detailed-header">
                  <FaLock />
                  {t('profile.changePassword')}
                </h2>
                <div className="form-field">
                  <label>{t('profile.currentPassword')}</label>
                  <Field name="currentPassword">
                    {({ input, meta }) => (
                      <>
                        <input {...input} type="password" />
                        {meta.touched && meta.error && (
                          <div className="error">{meta.error}</div>
                        )}
                      </>
                    )}
                  </Field>
                </div>
                <div className="form-field">
                  <label>{t('profile.newPassword')}</label>
                  <Field name="newPassword">
                    {({ input, meta }) => (
                      <>
                        <input {...input} type="password" />
                        {meta.touched && meta.error && (
                          <div className="error">{meta.error}</div>
                        )}
                      </>
                    )}
                  </Field>
                </div>
                <div className="form-field">
                  <label>{t('profile.confirmNewPassword')}</label>
                  <Field name="confirmPassword">
                    {({ input, meta }) => (
                      <>
                        <input {...input} type="password" />
                        {meta.touched && meta.error && (
                          <div className="error">{meta.error}</div>
                        )}
                      </>
                    )}
                  </Field>
                </div>
                <div className="form-actions">
                  <button className="btn primary update" type="submit">
                    {t('profile.update')}
                  </button>
                  {passwordMessage && (
                    <span className={`form-status ${passwordMessageError ? 'error' : ''}`} aria-live="polite">
                      {passwordMessage}
                    </span>
                  )}
                </div>
              </form>
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default Profile;
